import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { takeUntil } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { TranslatePipe } from '../../pipes/translate.pipe';
import { AssetModel } from '../../services/asset-list.service';
import { AuthService } from '../../services/auth.service';
import { CryptoService } from '../../services/crypto.service';
import { ModelService } from '../../services/model.service';
import { OverlayService } from '../../services/overlayService';
import { ProjectV2ServiceService } from '../../services/project-v2-service.service';
import { WorkspaceService } from '../../services/workspace.service';
import { UnSubscribeComponent } from '../un-subscribe/un-subscribe.component';
import { DeleteModelConfirmationModalComponent } from './delete-model-confirmation-modal/delete-model-confirmation-modal.component';
import { SaveModelConfirmationModalComponent } from './save-model-confirmation-modal/save-model-confirmation-modal.component';
import { AssetModelComponent } from '../asset-model/asset-model.component';

@Component({
  selector: 'mtm-manage-asset-list-modal',
  templateUrl: './manage-asset-list-modal.component.html',
  styleUrls: [
    './manage-asset-list-modal.component.scss',
    './manage-asset-list-actions/manage-asset-list-actions.component.scss',
  ],
  standalone: false,
})
export class ManageAssetListModalComponent extends UnSubscribeComponent implements OnInit, OnDestroy, OnChanges {
  @Input() rawAssets: any[];
  @Input() campaignId: string;
  @Output() modelExported = new EventEmitter<void>();
  @Output() refetchRawAssets = new EventEmitter<void>();

  private readonly cryptoService = inject(CryptoService);
  private readonly route = inject(ActivatedRoute);
  private readonly workspaceService = inject(WorkspaceService);
  private readonly authService = inject(AuthService);

  shouldRefetchRawAssets: boolean = false;
  workspaceId: string = '';
  workspaceName: string = '';
  models: AssetModel[] = [];
  selectedModel: AssetModel | null = null;
  isCreatingNew: boolean = false;
  newModelName: string = '';
  newModelNameError: string = '';
  newModelAssets: any[] = [];
  newModelDisplayAssets: any[] = [];
  isLoading: boolean = false;
  isEditing: boolean = false;
  isSavingTimeline: boolean = false;
  currentView: 'all' | 'newModel' = 'newModel';
  affectedAssets: any = {};
  expandedModels: { [key: string]: boolean } = {};
  editingModelId: string = '';
  useOnlyModels = ['1* - CPD MAY 25', '2* - CPD MAY 25', '3* - CPD MAY 25'];

  constructor(
    private readonly activeModal: NgbActiveModal,
    private readonly modelService: ModelService,
    private readonly overlayService: OverlayService,
    private readonly translatePipe: TranslatePipe,
    private readonly modalService: NgbModal,
    private readonly projectV2Service: ProjectV2ServiceService
  ) {
    super();
  }

  ngOnInit() {
    this.route.queryParams.pipe(takeUntil(this.unSubscribeOnDestroy)).subscribe({
      next: (query) => {
        this.workspaceId = this.cryptoService.decryptUsingAES256(query.wid);
        this.loadModels();
        this.getWorkspaceName();
      },
    });
    this.newModelAssets = [...this.rawAssets];
    this.newModelDisplayAssets = [...this.rawAssets];
    this.expandedModels = this.models.reduce((acc, model) => {
      acc[model.id] = false;
      return acc;
    }, {});
  }

  getWorkspaceName() {
    if (this.workspaceId) {
      this.workspaceService
        .getWorkspaceById(this.authService.getAuthUser().company.id, this.workspaceId)
        .pipe(takeUntil(this.unSubscribeOnDestroy))
        .subscribe({
          next: (workspace) => {
            if (workspace) {
              this.workspaceName = workspace.name;
            }
          },
        });
    }
  }

  get isExistedModelName() {
    return this.models.some(
      (m) => this.newModelName?.trim() && m.name?.trim().toLowerCase() === this.newModelName?.trim().toLowerCase()
    );
  }

  isRestrictedModel(name: string): boolean {
    return this.useOnlyModels.some((prefix) => name.startsWith(prefix));
  }

  sortItems(list: AssetModel[]): AssetModel[] {
    const getPriority = (name: string): number => {
      for (let i = 0; i < this.useOnlyModels.length; i++) {
        if (name.startsWith(this.useOnlyModels[i])) return i;
      }
      return this.useOnlyModels.length;
    };

    return list.sort((a, b) => {
      const priorityA = getPriority(a?.name || '');
      const priorityB = getPriority(b?.name || '');

      if (priorityA !== priorityB) return priorityA - priorityB;
      return 0;
    });
  }

  loadModels() {
    this.modelService
      .getModelsOfGivenWorkspace(this.workspaceId)
      .pipe(takeUntil(this.unSubscribeOnDestroy))
      .subscribe({
        next: (data: any) => {
          this.models = data.map((m) => ({
            ...m,
            assets: JSON.parse(m.assetList),
          }));
          this.models = this.sortItems(this.models);
        },
      });
  }

  toggleModel(modelId: string) {
    this.editingModelId = '';
    Object.keys(this.expandedModels).forEach((key) => {
      if (key !== modelId) {
        this.expandedModels[key] = false;
      }
    });
    this.expandedModels[modelId] = !this.expandedModels[modelId];
  }

  dismiss() {
    this.activeModal.dismiss();
    if (this.shouldRefetchRawAssets) {
      this.refetchRawAssets.emit();
    }
  }

  onCancel() {
    this.newModelAssets = [...this.rawAssets];
    this.newModelDisplayAssets = [...this.rawAssets];
    this.isEditing = false;
  }

  onSave() {
    this.isSavingTimeline = true;
    this.rawAssets = [...this.newModelAssets];
    this.projectV2Service
      .bulkUpdateCampaignAssets({
        id: this.campaignId,
        assetList: JSON.stringify(this.newModelAssets),
      })
      .pipe(takeUntil(this.unSubscribeOnDestroy))
      .subscribe({
        next: (result: any) => {
          this.isSavingTimeline = false;
          this.isEditing = false;
          this.shouldRefetchRawAssets = true;
        },
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['newModelName']) {
      this.newModelNameError = '';
    }
  }

  //#region New Model
  toggleEditNewModel() {
    this.isEditing = !this.isEditing;
  }

  get isSaveNewModelDisabled() {
    return (
      this.isLoading || this.newModelName.length === 0 || this.newModelAssets.length === 0 || this.isSavingTimeline
    );
  }

  handleSaveNewModel() {
    const modalRef = this.modalService.open(SaveModelConfirmationModalComponent, {
      size: 'sm',
      centered: true,
      backdrop: 'static',
      keyboard: false,
      modalDialogClass: 'save-model-modal',
    });

    const component = modalRef.componentInstance as SaveModelConfirmationModalComponent;
    component.workspaceName = this.workspaceName;
    component.modelName = this.newModelName;
    component.confirmSave.pipe(takeUntil(this.unSubscribeOnDestroy)).subscribe(() => {
      this.modelService
        .createNewModel(this.campaignId, {
          name: this.newModelName,
          assetIds: this.newModelAssets.map((a) => a.id),
        })
        .pipe(
          takeUntil(this.unSubscribeOnDestroy),
          finalize(() => {
            this.loadModels();
          })
        )
        .subscribe({
          next: (res) => {
            this.overlayService.showSuccess(this.translatePipe.transform('model_create_success'));
            this.currentView = 'all';
          },
          error: (err) => {
            console.error(err);
            this.newModelNameError = err?.message;
            this.overlayService.showError(err?.message || this.translatePipe.transform('model_create_fail'));
          },
        });
    });
  }
  handleImportModel() {
    this.currentView = 'all';
  }
  //#endregion

  //#region All Models
  handleBackToNewModel() {
    this.currentView = 'newModel';
  }
  handleEditModelClick(modelId: string, $event: Event) {
    $event.stopPropagation();
    this.editingModelId = modelId;
  }

  handleDeleteModelClick(modelId: string, $event: Event) {
    $event.stopPropagation();
    const model = this.models.find((m) => m.id === modelId);
    if (!model) return;

    const modalRef = this.modalService.open(DeleteModelConfirmationModalComponent, {
      size: 'sm',
      centered: true,
      backdrop: 'static',
      keyboard: false,
      modalDialogClass: 'delete-model-modal',
    });

    modalRef.componentInstance.modelName = model.name;
    modalRef.componentInstance.confirmDelete.subscribe(() => {
      // Remove the model from the list
      this.modelService
        .deleteModel(model.id)
        .pipe(
          takeUntil(this.unSubscribeOnDestroy),
          finalize(() => {
            this.loadModels();
          })
        )
        .subscribe({
          next: (data: any) => {
            this.overlayService.showSuccess(this.translatePipe.transform('model_delete_success'));
          },
          error: (error) => {
            console.error(error);
            this.overlayService.showError(this.translatePipe.transform('model_delete_fail'));
          },
        });
    });
  }

  handleUseModel(model: any, $event: Event) {
    $event.stopPropagation();

    const modalRef = this.modalService.open(AssetModelComponent, {
      backdrop: 'static',
      keyboard: false,
    });

    modalRef.componentInstance.title = 'Use this model';
    modalRef.componentInstance.description = `You are about to apply the <strong>"${model.name}"</strong> asset list model to this campaign. This action will replace the current asset list.<br><br> Are you sure you want to proceed?`;
    modalRef.componentInstance.confirmBtn = 'Save';
    modalRef.componentInstance.cancelBtn = 'Cancel';

    modalRef.result
      .then((confirm: any) => {
        if (!confirm) return;

        this.modelService
          .exportModelToCampaign(this.campaignId, model.id)
          .pipe(
            takeUntil(this.unSubscribeOnDestroy),
            finalize(() => {
              this.dismiss();
            })
          )
          .subscribe({
            next: () => {
              this.overlayService.showSuccess(this.translatePipe.transform('model_export_success'));
              this.modelExported.emit();
            },
            error: (err) => {
              console.error(err);
              this.overlayService.showError(this.translatePipe.transform('model_export_fail'));
            },
          });
      })
      .catch(() => {
        // Modal dismissed, do nothing
      });
  }

  //#endregion
  assetBundleUpdated(e) {
    console.log(e);
  }

  createUpdateAsset(asset: any, modelId?: string) {
    if (modelId) {
      this.modelService
        .saveAssetModel(modelId, asset)
        .pipe(
          takeUntil(this.unSubscribeOnDestroy),
          finalize(() => {
            this.loadModels();
          })
        )
        .subscribe({
          next: (res) => {
            this.overlayService.showSuccess(
              this.translatePipe.transform(asset.id ? 'model_asset_update_success' : 'model_asset_create_success')
            );
          },
          error: (err) => {
            console.error(err);
            this.overlayService.showError(
              this.translatePipe.transform(asset.id ? 'model_asset_update_fail' : 'model_asset_create_fail')
            );
          },
        });
    }
  }

  deleteAsset(asset: any, modelId?: string) {
    if (modelId) {
      this.modelService
        .deleteAssetModel(modelId, asset.id)
        .pipe(
          takeUntil(this.unSubscribeOnDestroy),
          finalize(() => {
            this.loadModels();
          })
        )
        .subscribe({
          next: (res) => {
            this.overlayService.showSuccess(this.translatePipe.transform('model_asset_delete_success'));
          },
          error: (err) => {
            console.error(err);
            this.overlayService.showError(this.translatePipe.transform('model_asset_delete_fail'));
          },
        });
    }
  }

  deleteSubAsset(asset: any, modelId?: string) {
    this.modelService
      .deleteSubAssetModel(modelId, asset.id, asset.subAssetId)
      .pipe(
        takeUntil(this.unSubscribeOnDestroy),
        finalize(() => {
          this.loadModels();
        })
      )
      .subscribe({
        next: (res) => {
          this.overlayService.showSuccess(this.translatePipe.transform('model_sub_asset_delete_success'));
        },
        error: (err) => {
          console.error(err);
          this.overlayService.showError(this.translatePipe.transform('model_sub_asset_delete_fail'));
        },
      });
  }
  assetBundleChanged(bundle: any, modelId?: string) {
    if (this.currentView === 'all') {
      return;
    }
    this.newModelAssets = JSON.parse(JSON.stringify(bundle || []));
    this.newModelDisplayAssets = JSON.parse(JSON.stringify(bundle || []));
  }

  bulkUpdateAssets(data: any, modelId?: string) {
    if (modelId) {
      this.modelService
        .bulkUpdateModelAssets(modelId, data)
        .pipe(
          takeUntil(this.unSubscribeOnDestroy),
          finalize(() => {
            this.loadModels();
          })
        )
        .subscribe({
          next: (res) => {
            this.overlayService.showSuccess(
              this.translatePipe.transform(`model_asset_${data.action}_success`.toLowerCase())
            );
          },
          error: (err) => {
            console.error(err);
            this.overlayService.showError(
              this.translatePipe.transform(`model_asset_${data.action}_fail`.toLowerCase())
            );
          },
        });
    }
  }
}
